import { StoreContext } from '../store';

import Cookies from 'js-cookie';
import moment from 'moment';
import ErrorPage from 'next/error';
import { useRouter } from 'next/router';
import { useContext, useEffect } from 'react';
import config from '../config';
import useFillStore from '../hooks/useFillStore';
import { setOrganizationId } from '../utils/fetch';
import ServiceUnavailable from './error/ServiceUnavailable';

import { observer } from 'mobx-react-lite';

async function fetchData(store, router) {
  if (store.user.signedIn) {
    return store.organization.items;
  }
  // await store.dashboard.fetch()
  // Fetch organization everytime the token in cookies is deleted
  await store.organization.fetch()
  return store.organization.items;
}

export function withAuthentication(PageComponent, grantedRole) {
  const WithAuth = observer((pageProps) => {
    const store = useContext(StoreContext);
    const router = useRouter();
    const [fetching] = useFillStore(fetchData, [router]);
    // const tenantStorage = JSON.parse(localStorage.getItem('Tenant') || '{}');
    // const tenantLength = tenantStorage?.items?.length || 0;

    useEffect(() => {
      if (store.organization.selected?.locale && Cookies.get('NEXT_LOCALE') !== store.organization.selected.locale) {
        Cookies.set('NEXT_LOCALE', store.organization.selected.locale, { expires: 365 });
      }
    }, [store.organization.selected?.locale]);

    if (fetching) {
      return null;
    }

    if (store.organization.selected?.locale && router.locale !== store.organization.selected.locale) {
      router.push({ pathname: router.pathname, query: router.query }, router.asPath, { locale: store.organization.selected.locale });
      return null;
    }

    if (store.user.signedIn === false) {
      window.location.assign(`${config.BASE_PATH}/signin`);
      window?.localStorage.clear();
      return null;
    }


    if (
      router.pathname !== '/firstaccess'
      &&
      !store.organization.items?.length
    ) {
      window.location.assign(`${config.BASE_PATH}/firstaccess`);
      return null;
    }
    
    const runPostLoginFlow = async () => {
      try {
        const response = await store.organization.fetch();

        if (response && store.organization.items.length) {
          if (!store.organization.selected) {
            await store.organization.setSelected(
              store.organization.items[0],
              store.user
            );
          }

          setOrganizationId(store.organization.selected._id);

          try {
            await store.rbac.getPermissions();
          } catch (e) {
            console.warn("RBAC fetch failed", e);
          }

          if (store.organization.selected.isFirstConnection) {
            window.location.assign(`${config.BASE_PATH}/onboarding`);
          } else {
            window.location.assign(`${config.BASE_PATH}/dashboard`);
          }

          await store.subscription.fetchData();
        } else {
          window.location.assign(`${config.BASE_PATH}/firstaccess`);
        }
      } catch (error) {
        console.error("Post login flow failed:", error);
      }
    };

    if (store.organization.serverDown) {
      return (
        <ServiceUnavailable onRetry={runPostLoginFlow}
        />
      );
    }

    // Check if user's access is expired (trial or subscription) - HIGHEST PRIORITY
    const isTrialExpired = store.user.trialEndDate ? moment(store.user.trialEndDate).isBefore(moment(), 'day') : false;
    const isSubscriptionExpired = store.user.subscriptionEndDate ? moment(store.user.subscriptionEndDate).isBefore(moment(), 'day') : false;
    const isAccessExpired = isTrialExpired || isSubscriptionExpired;
    const currentPath = router.pathname;

    // Allow access to specific pages when expired
    const allowedPathsWhenExpired = ['/trial-expired', '/signin', '/subscription'];

    if (!store.organization.serverDown && isAccessExpired && !allowedPathsWhenExpired.includes(currentPath)) {
      window.location.replace(`${config.BASE_PATH}/trial-expired`);
      return null;
    }

    // Only apply onboarding logic for non-expired users
    if (!isAccessExpired) {
      if (store.user.plan !== 'starter') {
        if (
          router.pathname !== '/onboarding' &&
          store.organization.items?.length && store.organization.selected?.isFirstConnection
        ) {
          // console.log('Redirecting trial user to onboarding...');
          window.location.replace(`${config.BASE_PATH}/onboarding`);
          return null;
        } else if (
          router.pathname === '/onboarding' &&
          store.organization.items?.length && !store.organization.selected?.isFirstConnection
        ) {
          // console.log('Redirecting trial user from onboarding to dashboard...');
          window.location.replace(`${config.BASE_PATH}/dashboard`);
          return null;
        }
      } else {
        // If non-trial user is on onboarding page, redirect to property page
        if (router.pathname === '/onboarding') {
          window.location.replace(`${config.BASE_PATH}/properties`);
          return null;
        }
      }
    } else {
      // console.log('Skipping onboarding logic for expired user');
    }

    if (grantedRole && grantedRole !== store.user.role) {
      return <ErrorPage statusCode={404} />;
    }

    return <PageComponent {...pageProps} />;
  });

  return WithAuth;
}
